/**
 * Credit Management React Query hooks
 * Uses Gobodo via recordStorageService when USE_GOBODO_API is enabled.
 */

import { useQuery } from '@tanstack/react-query';
import { getRecordsForObject } from '../recordStorageService';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import type { CreditRepairClient, IdTheftClient, CreditDevelopmentClient } from '../../types';

const CLIENT_OBJECT = 'Client__c';

const mapToCreditRepair = (r: Record<string, unknown>): CreditRepairClient => {
  const initial = Number(r.InitialCreditScore__c ?? r.initialCreditScore ?? 0);
  const current = Number(r.CurrentCreditScore__c ?? r.currentCreditScore ?? initial);
  const target = Number(r.TargetCreditScore__c ?? r.targetCreditScore ?? 720);
  return {
    id: String(r.id ?? ''),
    name: String(r.Name__c ?? r.name ?? ''),
    status: String(r.Status__c ?? r.status ?? 'In Progress'),
    initialScore: initial,
    currentScore: current,
    targetScore: target,
    improvement: Math.max(0, current - initial),
    scoreImprovement: Math.max(0, current - initial),
    creditMonitoring: Boolean(r.CreditMonitoring__c ?? r.creditMonitoring ?? true),
  };
};

const mapToIdTheft = (r: Record<string, unknown>): IdTheftClient => {
  const fraudulentData = (r.FraudulentData__c ?? r.fraudulentData) as Array<Record<string, unknown>> | undefined;
  const accounts = Array.isArray(fraudulentData) ? fraudulentData : [];
  return {
    id: String(r.id ?? ''),
    name: String(r.Name__c ?? r.name ?? ''),
    status: String(r.Status__c ?? r.status ?? 'Active Investigation'),
    fraudulentAccounts: Number(r.FraudulentAccounts__c ?? r.fraudulentAccounts ?? accounts.length),
    lastUpdate: String(r.LastUpdate__c ?? r.lastUpdate ?? r.LastModifiedDate__c ?? ''),
    fraudulentData: accounts.map(a => ({
      type: String(a.type ?? a.Type__c ?? ''),
      creditor: String(a.creditor ?? a.Creditor__c ?? ''),
      amount: String(a.amount ?? a.Amount__c ?? ''),
      dateOpened: String(a.dateOpened ?? a.DateOpened__c ?? ''),
      status: String(a.status ?? a.Status__c ?? ''),
    })),
  };
};

const mapToCreditDevelopment = (r: Record<string, unknown>): CreditDevelopmentClient => {
  const creditCards = (r.CreditCards__c ?? r.creditCards) as Array<Record<string, unknown>> | undefined;
  const cards = Array.isArray(creditCards) ? creditCards : [];
  return {
    id: String(r.id ?? ''),
    name: String(r.Name__c ?? r.name ?? ''),
    creditHistory: String(r.CreditHistory__c ?? r.creditHistory ?? 'No Credit History'),
    status: String(r.Status__c ?? r.status ?? 'Building Credit'),
    currentScore: Number(r.CurrentCreditScore__c ?? r.currentCreditScore ?? 0),
    targetScore: Number(r.TargetCreditScore__c ?? r.targetCreditScore ?? 650),
    authorizedUserAccounts: Number(r.AuthorizedUserAccounts__c ?? r.authorizedUserAccounts ?? 0),
    creditMonitoring: String(r.CreditMonitoring__c ?? r.creditMonitoring ?? 'Awaiting Setup'),
    creditCards: cards.map(c => ({
      name: String(c.name ?? c.Name__c ?? ''),
      status: String(c.status ?? c.Status__c ?? ''),
    })),
  };
};

const hasProduct = (r: Record<string, unknown>, product: string): boolean => {
  const products = r.Products__c ?? r.products;
  if (Array.isArray(products)) return products.some((p: unknown) => String(p).toLowerCase().includes(product.toLowerCase()));
  if (typeof products === 'string') return products.toLowerCase().includes(product.toLowerCase());
  const serviceType = String(r.ServiceType__c ?? r.serviceType ?? '').toLowerCase();
  const program = String(r.Program__c ?? r.program ?? '').toLowerCase();
  const p = product.toLowerCase();
  return serviceType.includes(p) || program.includes(p);
};

export function useCreditRepairClients() {
  return useQuery({
    queryKey: ['credit-management', 'credit-repair-clients'],
    queryFn: async () => {
      const records = await getRecordsForObject(CLIENT_OBJECT);
      return (records as Record<string, unknown>[])
        .filter(r => hasProduct(r, 'Credit Repair'))
        .map(mapToCreditRepair);
    },
    enabled: USE_GOBODO_API,
    staleTime: 60_000,
  });
}

export function useIdTheftClients() {
  return useQuery({
    queryKey: ['credit-management', 'id-theft-clients'],
    queryFn: async () => {
      const records = await getRecordsForObject(CLIENT_OBJECT);
      return (records as Record<string, unknown>[])
        .filter(r => hasProduct(r, 'ID Theft') || hasProduct(r, 'Identity Theft'))
        .map(mapToIdTheft);
    },
    enabled: USE_GOBODO_API,
    staleTime: 60_000,
  });
}

export function useCreditDevelopmentClients() {
  return useQuery({
    queryKey: ['credit-management', 'credit-development-clients'],
    queryFn: async () => {
      const records = await getRecordsForObject(CLIENT_OBJECT);
      return (records as Record<string, unknown>[])
        .filter(r => hasProduct(r, 'Credit Development'))
        .map(mapToCreditDevelopment);
    },
    enabled: USE_GOBODO_API,
    staleTime: 60_000,
  });
}
